import {
    MultipleRadio,
    Utils,
    MultiTagSelectV2,
    Hooks,
    // TreeSelect,
    CompanyIndustry,
    JobFunction,
    CheckBox,
    useHog,
    SingleCheckBox,
    Tooltip,
    Paragraph,
    colors,
    Images,
    Button,
    Switcher,
    Skeleton
} from '@microfrontends/react-components'
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import {
    debounce,
    setCookie,
    shouldTrackOncePerSession,
    getPreferredSortIndexName
} from '../../../../helpers/common'
import SearchResultContext from '../../context'
import filterHelper, { fireGlobalSearchEvent, TSelectData } from '../../helpers/filterHelper'
import { useTranslation } from '../../hooks'
import { IconFilter, IconFilterFilled, IconSortDesc } from '../../icons'
import { TRootState } from '../../searchResultsRootReducers'
import { EPageTab } from '../../types/EPageTag'
import { ISearchFilterParams } from '../../types/ISearchFilterParams'
import { ELanguage } from '../../utils/language'
import translations from './../../translations.json'
import { FilterWrapper, StyledFilterBar, MoreTitle, SelectedBenefit, Clear, StyledFilterSalary } from './styled'
import { initialDataProvider } from '@microfrontends/app-shell-v2'
import MoreFilterButton from '../MoreFilterButton'
import { ESortingHighlightTooltip, ETooltipType, HIGHTLIGHT_SORTING_TOOLTIP_NAME } from '../../../SearchSorting'
import { getCookie, isMobile, isPhone, isSmallPhone } from '../../utils/utilities'
import { TooltipDes } from '../../../SearchSorting/styles'
import {
    MANAGEMENT_JOB_BY_DATE,
    MANAGEMENT_JOB_BY_SALARY,
    MANAGEMENT_JOB_DEFAULT,
    SORT_RELEVANCE_INDEX
} from '../../../../env'
import { SORT_DEFAULT_INDEX, SORT_POSTED_LATEST_INDEX, SORT_POSTED_OLDEST_INDEX, SORT_SALARY_INDEX } from './SortDialog'
import { TAuthUser } from '../../../../handlers/auth/interface'
import { useQuickSalaryFilter } from '../../../SearchResultV2/hooks/useQuickSalaryFilter'
import { APPEAR_SORT_RELEVANT_TOOLTIP_TRACK_KEY, DEFAULT_SALARY } from '../../../SearchResultV2/constants'
import { useExperiment8Freshness } from '../../../SearchResultV2/context/Experiment8FreshnessContext'
import SortBarExtraFilterRows from '../../../SearchResultV2/components/SortBarExtraFilterRows'
import { buildExperiment8ExtraFilterSearchProps } from '../../../SearchResultV2/helpers/experiment8ExtraFilterTracking'

const delayedFireGlobalSearchEvent = debounce(fireGlobalSearchEvent, 300)

const defaultParams = filterHelper.getDefaultFilterableParams()

type TFilterBarProps = {
    onClickFilter?: () => void
    onClickSort?: () => void
    sortingOptions: {
        sortId: string | number
        isManagementJob: boolean
        showSortingHighlightMobile: boolean
    }
    user: TAuthUser
}

const ALL_VALUE = -1

export default function FilterBar({ onClickFilter, onClickSort, sortingOptions, user }: TFilterBarProps) {

    const { langCode } = useContext(SearchResultContext)
    const { tracking, trackingQueue } = useHog()
    const { isVersionA: isExperiment8VersionA, isExperiment8Ready } = useExperiment8Freshness()

    const [selectData, lastParams] = useSelector<any, [TSelectData, ISearchFilterParams]>((state) => {
        return [state.searchFilter.selectData, state.searchFilter.lastParams]
    })


    const { salary: salaryOptions = [], level = [], typeWorking = [] } = selectData
    const industries = initialDataProvider.getIndustries()
    const jobFunctions = initialDataProvider.getJobFunction()
    const jobCount = initialDataProvider.getJobFunctionCount()
    const {
        benefitIds = [],
        isUrgentJob,
        categoryId,
        salary,
        jobLevelId,
        typeWorkingId,
        jobFunctionId,
        companyIndustries,
        locations
    } = lastParams

    const pageTab = useSelector<TRootState, EPageTab>((state) => state.globalMisc.pageTab)
    const translate = useTranslation(translations)
    const [showMoreFilter, setShowMoreFilter] = useState(false)
    const [isMoreFilterOpen, setIsMoreFilterOpen] = useState(false)
    const [moreFilterSaveOptions, setMoreFilterSaveOptions] = useState({
        salary: [-1, -1],
        benefit: [],
        companyIndustries: [],
        jobLevelId: -1,
        typeWorkingId: -1,
        jobFunctionId: [],
        isUrgentJob: false
    }) // save options of more filter when open drop down
    const latestMoreFilterSalaryRef = useRef<number[]>(moreFilterSaveOptions.salary)
    const [windowWidth, setWindowWidth] = useState<number>(768)
    const matcher = Hooks.useMediaQuery('(max-width: 767px)')
    const [isShowSorting, setIsShowSorting] = useState<boolean>(false)
    const [isHighlightTooltip, setIsHighlightTooltip] = useState<boolean>(false)
    const [isFilterBarFixed, setFilterBarFixed] = useState<boolean>(false)
    const [extraFilterCalloutDismissed, setExtraFilterCalloutDismissed] = useState<boolean>(false)
    const switcherRef = useRef<HTMLDivElement>(null)
    const prevIsShowTooltipRef = useRef(false)
    const sortDesktopRef = useRef<HTMLDivElement>(null)
    const sortMobileRef = useRef<HTMLDivElement>(null)
    const [isSortTooltipInView, setIsSortTooltipInView] = useState(false)

    const preferredIndexName = getPreferredSortIndexName()
    const isLoggedIn = Boolean(user?.accessToken)
    const sortId =
        lastParams?.indexName && lastParams?.indexName !== 'default'
            ? lastParams?.indexName
            : (isLoggedIn ? sortingOptions.sortId : preferredIndexName || sortingOptions.sortId)
  
    const {
        isQuickSalaryFilterOn,
        isQuickSalaryTooltipVisible,
        shouldShowQuickSalaryFilter,
        isQuickSalaryFilterLoading,
        isControl,
        quickSalaryFilterConfig,
        handleCloseQuickSalaryTooltip,
        handleQuickSalaryFilterToggle,
        convertSalaryValue
    } = useQuickSalaryFilter({
        user,
        lastParams,
        translate,
        isHighlightTooltip,
        sortId,
        SORT_RELEVANCE_INDEX,
        isShowSorting,
        isSuspendQuickSalarySync: isMoreFilterOpen,
        hideQuickSalaryPromoUI: isExperiment8VersionA || !isExperiment8Ready
    })

    const showQuickSalarySkeleton = !isControl && isQuickSalaryFilterLoading

    /*
     * Placement for More button
     * */
    const { refs } = Hooks.useFloating({
        placement: 'bottom-start',
        config: {
            isFlip: false,
            isShift: true
        }
    })

    const renderLevel = useMemo(() => {
        const isTopManagementJobPageTab: boolean = pageTab === EPageTab.MANAGEMENT_JOBS
        const managementLevelIds = [7, 3]
        const cpLevel = [...level]
        const levelOptions = isTopManagementJobPageTab
            ? cpLevel.filter((item) => managementLevelIds.includes(item.id))
            : cpLevel
        levelOptions.unshift({
            id: ALL_VALUE,
            name: translate('All levels')
        })
        return levelOptions
    }, [level, pageTab, translate])


    const onWindowResize = () => {
        const windowWidth = typeof window !== 'undefined' ? window.innerWidth : 768
        setWindowWidth(windowWidth)
    }

    useEffect(() => {
        const handleClickOnScreen = (e: MouseEvent) => {
            const isClickOutSideDropdown = isClickOutside(e, refs.popup)
            if (showMoreFilter && isClickOutSideDropdown) {
                setShowMoreFilter(false)
            }
        }

        document.addEventListener('keydown', handleKeydown)
        document.addEventListener('mouseup', handleClickOnScreen)

        return () => {
            document.removeEventListener('keydown', handleKeydown)
            document.removeEventListener('mouseup', handleClickOnScreen)
        }
    }, [showMoreFilter])

    /**
     * VNW-17742 Not reset filter after select skill tag
     *
     * @author Tri, Tran Minh
     * @since 5 sept, 2022
     */
    useEffect(() => {
        const benefitChange = !Utils.compareVariable(benefitIds, moreFilterSaveOptions.benefit)
        const salaryChange = !Utils.compareVariable(salary, moreFilterSaveOptions.salary)
        const jobLevelIdChange = !Utils.compareVariable(jobLevelId, moreFilterSaveOptions.jobLevelId)
        const companyIndustriesChange = !Utils.compareVariable(
            companyIndustries,
            moreFilterSaveOptions.companyIndustries
        )
        const typeWorkingIdChange = !Utils.compareVariable(typeWorkingId, moreFilterSaveOptions.typeWorkingId)
        const jobFunctionIdChange = !Utils.compareVariable(jobFunctionId, moreFilterSaveOptions.jobFunctionId)
        const isUrgentJobChange = isUrgentJob !== moreFilterSaveOptions.isUrgentJob

        let filterSaveOptions = moreFilterSaveOptions
        let isChange = false

        if (benefitChange) {
            filterSaveOptions = { ...filterSaveOptions, benefit: benefitIds }
            isChange = true
        }
        if (salaryChange) {
            filterSaveOptions = { ...filterSaveOptions, salary }
            isChange = true
        }
        if (jobLevelIdChange) {
            filterSaveOptions = { ...filterSaveOptions, jobLevelId }
            isChange = true
        }
        if (companyIndustriesChange) {
            filterSaveOptions = { ...filterSaveOptions, companyIndustries }
            isChange = true
        }
        if (typeWorkingIdChange) {
            filterSaveOptions = { ...filterSaveOptions, typeWorkingId }
            isChange = true
        }
        if (jobFunctionIdChange) {
            filterSaveOptions = { ...filterSaveOptions, jobFunctionId }
            isChange = true
        }

        if (isUrgentJobChange) {
            filterSaveOptions = { ...filterSaveOptions, isUrgentJob }
            isChange = true
        }

        if (isChange) {
            setMoreFilterSaveOptions(filterSaveOptions)
        }
    }, [benefitIds, salary])

    useEffect(() => {
        latestMoreFilterSalaryRef.current = moreFilterSaveOptions.salary
    }, [moreFilterSaveOptions.salary])

    useEffect(() => {
        onWindowResize()
        window.addEventListener('resize', onWindowResize)
        return () => {
            window.removeEventListener('resize', onWindowResize)
        }
    }, [])

    const handleSalaryChange = (response) => {
        const textId = String(response.value)
        const salaryId =
            textId === '-1'
                ? [-1, -1]
                : textId.split(',').map((salaryNumber) => parseInt(salaryNumber))
        const normalizedSalaryId = salaryId.length === 1 ? [salaryId[0], 0] : salaryId

        latestMoreFilterSalaryRef.current = normalizedSalaryId
        setMoreFilterSaveOptions({ ...moreFilterSaveOptions, salary: normalizedSalaryId })
    }

    const handleBenefitChange = (benefit) => {
        setMoreFilterSaveOptions({ ...moreFilterSaveOptions, benefit })
    }

    const handleClearBenefit = () => {
        setMoreFilterSaveOptions({
            ...moreFilterSaveOptions,
            benefit: []
        })
    }

    const handleMoreFilterApply = () => {
        const appliedSalary = latestMoreFilterSalaryRef.current
        const experiment8Properties = buildExperiment8ExtraFilterSearchProps({
            postedWithin: lastParams.postedWithin,
            applicantsLessThan: lastParams.applicantsLessThan
        })
        delayedFireGlobalSearchEvent(
            {
                benefitIds: moreFilterSaveOptions.benefit as number[],
                salary: appliedSalary,
                jobLevelId: moreFilterSaveOptions.jobLevelId,
                companyIndustries: moreFilterSaveOptions.companyIndustries,
                typeWorkingId: moreFilterSaveOptions.typeWorkingId,
                jobFunctionId: moreFilterSaveOptions.jobFunctionId,
                isUrgentJob: moreFilterSaveOptions.isUrgentJob,
                page: 0
            },
            'FilterBar:Benefit&Salary'
        )
        tracking({
            eventName: 'js_search_result_page:filter_search_result',
            properties: {
                jobFunctionId: moreFilterSaveOptions.jobFunctionId,
                companyIndustryId: moreFilterSaveOptions.companyIndustries,
                jobLevelId: moreFilterSaveOptions.jobLevelId,
                workingTypeId: moreFilterSaveOptions.typeWorkingId,
                salaryRange: appliedSalary,
                isUrgent: moreFilterSaveOptions.isUrgentJob,
                keyword: lastParams.query,
                locationId: lastParams.locationId,
                districtId: lastParams.districtId,
                ...experiment8Properties
            }
        })
        setShowMoreFilter(false)
    }

    const handleKeydown = (e) => {
        if (typeof document !== 'undefined') {
            // TODO: need to re-check type of event (keyboardEvent)
            if (showMoreFilter && e.key === 'Tab') {
                const lastTag: any = document.querySelector('.tags-container')?.lastChild
                if (lastTag && lastTag === document.activeElement) {
                    setShowMoreFilter(false)
                }
            }
            if (showMoreFilter && e.key === 'Escape') {
                setShowMoreFilter(false)
            }
        }
    }

    const isClickOutside = (event, ref) => {
        if (ref.current) {
            if (ref.current.contains(event.target)) return false
        }
        return true
    }

    const handleClickClearAll = () => {
        delayedFireGlobalSearchEvent(
            {
                ...defaultParams,
                locationId: [],
                locations: [],
                districtId: [],
                postedWithin: '',
                applicantsLessThan: ''
            },
            'FilterBar:ClearAll'
        )
    }

    const handleInlineClearAllFilters = () => {
        delayedFireGlobalSearchEvent(
            {
                ...defaultParams,
                locationId: lastParams.locationId,
                locations: lastParams.locations,
                districtId: lastParams.districtId,
                indexName: lastParams.indexName,
                postedWithin: '',
                applicantsLessThan: ''
            },
            'FilterBar:InlineClearAllWithoutLocation'
        )
    }

    const handleOnClose = () => {
        setIsMoreFilterOpen(false)
        setMoreFilterSaveOptions({
            salary: salary,
            benefit: benefitIds,
            companyIndustries: companyIndustries,
            jobLevelId: jobLevelId,
            typeWorkingId: typeWorkingId,
            jobFunctionId: jobFunctionId,
            isUrgentJob: isUrgentJob
        })
    }

    const handleOnOpen = () => {
        setIsMoreFilterOpen(true)
        const currentSalary = Array.isArray(salary) ? salary : [-1, -1]
        latestMoreFilterSalaryRef.current = currentSalary
        setMoreFilterSaveOptions({
            salary: currentSalary,
            benefit: benefitIds,
            companyIndustries: companyIndustries,
            jobLevelId: jobLevelId,
            typeWorkingId: typeWorkingId,
            jobFunctionId: jobFunctionId,
            isUrgentJob: isUrgentJob
        })
    }

    const handleFilterChange = (filterOptions, from) => {
        const newFilterOptions = {
            ...lastParams,
            ...filterOptions
        }
        const experiment8Properties = buildExperiment8ExtraFilterSearchProps({
            postedWithin: newFilterOptions.postedWithin,
            applicantsLessThan: newFilterOptions.applicantsLessThan
        })

        delayedFireGlobalSearchEvent(filterOptions, from)

        tracking({
            eventName: 'js_search_result_page:filter_search_result',
            properties: {
                jobFunctionId: newFilterOptions.jobFunctionId,
                companyIndustryId: newFilterOptions.companyIndustries,
                jobLevelId: newFilterOptions.jobLevelId,
                workingTypeId: newFilterOptions.typeWorkingId,
                salaryRange: newFilterOptions.salary,
                isUrgent: newFilterOptions.isUrgentJob,
                keyword: lastParams.query,
                locationId: lastParams.locationId,
                districtId: lastParams.districtId,
                ...experiment8Properties
            }
        })
    }

    const filterNumber = useMemo<number>(() => {
        let hasFilter = 0


        if (categoryId.length > 0) {
            hasFilter++
        }
        if (jobLevelId > -1) {
            hasFilter++
        }
        if (convertSalaryValue(salary) !== -1) {
            // a trick cause salary is an exceptional case
            const isQuickSalaryRangeCurrent = salary[0] === quickSalaryFilterConfig.salaryValue && salary[1] === 0
            const isQuickSalaryRangeDefault = salary[0] === DEFAULT_SALARY && salary[1] === 0
            const isQuickSalaryRange = isQuickSalaryRangeCurrent || isQuickSalaryRangeDefault
            const shouldExcludeQuickSalary = isQuickSalaryRange && (!shouldShowQuickSalaryFilter || !isQuickSalaryFilterOn)

            if (!shouldExcludeQuickSalary) {
                hasFilter++
            }
        }
        if (isUrgentJob) {
            hasFilter++
        }
        if (typeWorkingId > -1) {
            hasFilter++
        }
        if (benefitIds.length > 0) {
            hasFilter++
        }
        if (companyIndustries.length > 0) {
            hasFilter++
        }
        if (jobFunctionId.length > 0) {
            hasFilter++
        }
        if (matcher && locations.length > 0) {
            hasFilter++
        }

        return hasFilter
    }, [
        matcher,
        categoryId,
        jobLevelId,
        salary,
        isUrgentJob,
        typeWorkingId,
        benefitIds,
        companyIndustries,
        jobFunctionId,
        locations,
        convertSalaryValue,
        isQuickSalaryFilterOn,
        shouldShowQuickSalaryFilter,
        quickSalaryFilterConfig.salaryValue
    ])



    const showStickySortingRow = isShowSorting || isExperiment8VersionA
    const hasExperiment8ExtraFilterSelected = useMemo(() => {
        const postedSelected =
            lastParams.postedWithin === '24h' || lastParams.postedWithin === '3d' || lastParams.postedWithin === '7d'
        const applicantsSelected = lastParams.applicantsLessThan === '5' || lastParams.applicantsLessThan === '20'
        return postedSelected || applicantsSelected
    }, [lastParams.applicantsLessThan, lastParams.postedWithin])
    const hasClearableFilterActive = useMemo(() => {
        let count = filterNumber
        if (matcher && locations.length > 0) {
            count = Math.max(0, count - 1)
        }
        return count > 0 || hasExperiment8ExtraFilterSelected
    }, [filterNumber, matcher, locations.length, hasExperiment8ExtraFilterSelected])
    const hideExperiment8FilterHighlight = extraFilterCalloutDismissed || hasExperiment8ExtraFilterSelected

    const benefitSelected =
        moreFilterSaveOptions.benefit.length > 1 ? translate('benefits selected') : translate('benefit selected')

    const companyIndustriesValue =
        moreFilterSaveOptions.companyIndustries.length > 0 ? moreFilterSaveOptions.companyIndustries[0] : -1

    const onScroll = () => {
        if (!isMobile()) {
            const heightToScrollOffSorting = 70
            const filterBarEl = document.querySelector('.job-sorting-section')
            if (!filterBarEl) {
                return
            }
            const boundingRect = filterBarEl.getBoundingClientRect()
            if (boundingRect.top <= heightToScrollOffSorting) {
                setIsShowSorting(true)
            } else {
                setIsShowSorting(false)
            }
        }

        if (isMobile()) {
            const headerEl = document.querySelector('[data-cname="Header"]')
            let heightToScrollOffSortingMobile = 65
            if (!isPhone()) {
                heightToScrollOffSortingMobile = 0
            }
            if (!headerEl) {
                return
            }
            const headerRectEl = headerEl.getBoundingClientRect()
            if (headerRectEl.top + headerRectEl.height <= heightToScrollOffSortingMobile) {
                setFilterBarFixed(true)
            } else {
                setFilterBarFixed(false)
            }
        }
    }
    useEffect(() => {
        // Initial cookie
        handleHighlightTooltip()

        // Add scroll event listener
        window.addEventListener('scroll', onScroll)
        window.addEventListener('resize', onScroll)

        // Cleanup event listener on component unmount
        return () => {
            window.removeEventListener('scroll', onScroll)
            window.removeEventListener('resize', onScroll)
        }
    }, [])
    const handleHighlightTooltip = () => {
        const sortingTooltip = getCookie(HIGHTLIGHT_SORTING_TOOLTIP_NAME)
        if (sortingTooltip === null || sortingTooltip === (ESortingHighlightTooltip.SHOW as string)) {
            setIsHighlightTooltip(true)
            if (sortingTooltip === null) {
                setCookie(HIGHTLIGHT_SORTING_TOOLTIP_NAME, ESortingHighlightTooltip.SHOW as string, ETooltipType.IN_DAY)
            }
            return
        }

        if (sortingTooltip === 'hidden') {
            setIsHighlightTooltip(false)
            return
        }
    }


    useEffect(() => {
        handleLabelSort()
        handleHighlightTooltip()
    }, [sortId])

    useEffect(() => {
        if (!isExperiment8VersionA || typeof window === 'undefined') {
            return
        }
        try {
            if (localStorage.getItem('vnw-e8-extra-filter-callout-dismissed') === '1') {
                setExtraFilterCalloutDismissed(true)
            }
        } catch {
            // ignore quota / private mode
        }
    }, [isExperiment8VersionA])

    useEffect(() => {
        if (!isExperiment8VersionA || !hasExperiment8ExtraFilterSelected || extraFilterCalloutDismissed) {
            return
        }
        if (typeof window !== 'undefined') {
            try {
                localStorage.setItem('vnw-e8-extra-filter-callout-dismissed', '1')
            } catch {
                // ignore quota / private mode
            }
        }
        setExtraFilterCalloutDismissed(true)
    }, [extraFilterCalloutDismissed, hasExperiment8ExtraFilterSelected, isExperiment8VersionA])

    const dismissExtraFilterCallout = () => {
        if (typeof window !== 'undefined') {
            try {
                localStorage.setItem('vnw-e8-extra-filter-callout-dismissed', '1')
            } catch {
                // ignore quota / private mode
            }
        }
        setExtraFilterCalloutDismissed(true)
    }

    useEffect(() => {
        const desktopEl = sortDesktopRef.current
        const mobileEl = sortMobileRef.current
        const refs = [desktopEl, mobileEl].filter(Boolean) as Element[]
        if (refs.length === 0) return
        const observer = new IntersectionObserver(
            (entries) => {
                const hasVisible = entries.some((e) => e.isIntersecting)
                if (hasVisible) setIsSortTooltipInView(true)
            },
            { threshold: 0.2, rootMargin: '0px' }
        )
        refs.forEach((el) => observer.observe(el))
        return () => refs.forEach((el) => observer.unobserve(el))
    }, [isShowSorting])

    const renderTooltipDes = () => {
        return (
            <TooltipDes>
                <Paragraph name={'body'} size={isSmallPhone() ? 'small' : 'medium'} color={colors.grey[100]}>
                    {translate('Select to prioritize viewing most relevant jobs')}
                </Paragraph>
                <div
                    onClick={() => {
                        setIsHighlightTooltip(false)
                        setCookie(
                            HIGHTLIGHT_SORTING_TOOLTIP_NAME,
                            ESortingHighlightTooltip.HIDDEN as string,
                            ETooltipType.IN_DAY
                        )
                    }}
                >
                    <Images.CloseIcon width={18} height={18} fill={colors.grey['100']} />
                </div>
            </TooltipDes>
        )
    }


    const renderQuickSalaryTooltipDes = () => {
        const formattedSalary = `$${quickSalaryFilterConfig.salaryValue.toLocaleString('en-US')}`
        const tooltipText = translate('Turn on to view jobs with salary above ') + formattedSalary

        return (
            <TooltipDes>
                <Paragraph name={'body'} size={isSmallPhone() ? 'small' : 'medium'} color={colors.grey[100]}>
                    {tooltipText}
                </Paragraph>
                <div
                    className='flex relative right-0 top-0 items-center flex-shrink-0 '
                    onClick={handleCloseQuickSalaryTooltip}
                >
                    <Images.CloseIcon width={18} height={18} fill={colors.grey['100']} />
                </div>
            </TooltipDes>
        )
    }
    const sortOptions = () => {
        const normalJobsSortOptions = [
            {
                id: SORT_DEFAULT_INDEX,
                name: translate('Last update')
            },
            {
                id: SORT_RELEVANCE_INDEX,
                name: translate('Most relevant'),
                isHighlight: true
            },
            {
                id: SORT_SALARY_INDEX,
                name: translate('Salary (high - low)')
            },
            {
                id: SORT_POSTED_LATEST_INDEX,
                name: translate('Posted date (latest)')
            },
            {
                id: SORT_POSTED_OLDEST_INDEX,
                name: translate('Oldest')
            }
        ]

        const managementJobsSortOptions = [
            {
                id: MANAGEMENT_JOB_DEFAULT,
                name: translate('All')
            },
            {
                id: MANAGEMENT_JOB_BY_DATE,
                name: translate('Posted date (latest)')
            },
            {
                id: MANAGEMENT_JOB_BY_SALARY,
                name: translate('Salary (high - low)')
            }
        ]

        const list = sortingOptions?.isManagementJob ? managementJobsSortOptions : normalJobsSortOptions
        if (!sortingOptions?.isManagementJob && isExperiment8VersionA) {
            return list.filter((item) => item.id !== SORT_POSTED_OLDEST_INDEX)
        }
        return list
    }
    const handleLabelSort = () => {
        let text = translate('Sort')
        const sortItem = sortOptions().filter((item) => item.id === sortId)
        if (sortItem.length > 0) {
            text = sortItem[0].name
        }
        return text
    }

    const isShowTooltip =
        !sortingOptions?.isManagementJob &&
        isHighlightTooltip &&
        sortingOptions.showSortingHighlightMobile &&
        sortId !== SORT_RELEVANCE_INDEX

    const isTooltipVisible = isShowTooltip && isSortTooltipInView

    useEffect(() => {
        const wasShown = prevIsShowTooltipRef.current
        prevIsShowTooltipRef.current = isTooltipVisible
        if (!isTooltipVisible || wasShown) {
            return
        }
        if (!shouldTrackOncePerSession(APPEAR_SORT_RELEVANT_TOOLTIP_TRACK_KEY)) {
            return
        }
        // trackingQueue({
        //     eventName: 'js_search_result_page:appear_sort_relevant_tooltip'
        // })
    }, [isTooltipVisible, trackingQueue])

    let styleSortingFixed: React.CSSProperties = {
        position: 'fixed',
        top: 'var(--app-shell-header-height, 64px)',
        width: '100%',
        zIndex: 10,
        marginTop: '8px'
    }
    if (isMobile() && !isPhone()) {
        styleSortingFixed = { position: 'fixed', top: '0', width: '100%', zIndex: 10, margin: '10px -8px 0' }
    }


    // Only show "Lương trên $1,000" option when quick filter experiment is active AND Switcher is ON
    const normalizedSalaryOptions = useMemo(() => {
        let result = salaryOptions.map((salaryOption) => {
            return {
                id: salaryOption.id.join(','),
                name: salaryOption.name
            }
        })

        // 1. Experiment is active (shouldShowQuickSalaryFilter = true)
        // 2. Switcher is ON (isQuickSalaryFilterOn = true)
        const shouldShowQuickSalaryOption = shouldShowQuickSalaryFilter && isQuickSalaryFilterOn

        if (!shouldShowQuickSalaryOption) {
            result = result.filter((opt) => {
                const optId = String(opt.id)
                return !(optId === '1000,0' || optId === `${quickSalaryFilterConfig.salaryValue},0`)
            })
        } else {
            const quickSalaryOptionId = `${quickSalaryFilterConfig.salaryValue},0`
            const quickOptionExists = result.some(opt => String(opt.id) === `${quickSalaryFilterConfig.salaryValue},0`)

            if (!quickOptionExists) {
                result.push({
                    id: quickSalaryOptionId,
                    name: quickSalaryFilterConfig.label
                })
            } else {
                const existingIndex = result.findIndex(opt => String(opt.id) === `${quickSalaryFilterConfig.salaryValue},0`)
                if (existingIndex >= 0) {
                    result[existingIndex].name = quickSalaryFilterConfig.label
                }
            }
        }

        result.unshift({
            id: ALL_VALUE as any,
            name: translate('All salary ranges')
        })
        return result
    }, [isQuickSalaryFilterOn, salaryOptions, translate, shouldShowQuickSalaryFilter, quickSalaryFilterConfig])


    const moreFilterSalaryValueRaw = convertSalaryValue(moreFilterSaveOptions.salary)
    const moreFilterSalaryValue = moreFilterSalaryValueRaw == -1 ? -1 : moreFilterSalaryValueRaw



    return (
        <>
            <StyledFilterBar data-name='StyledFilterBar' $language={langCode}>
                <div className='desktop desktop-filter-bar'>
                    <div className={'desktopFilter'}>
                        <div className='left-bar'>
                            {showQuickSalarySkeleton ? (
                                <>
                                    <div>
                                        <Skeleton width={144} height={40} />
                                    </div>
                                    <div className='action'>
                                        <Skeleton width={160} height={40} />
                                    </div>
                                    <div className='action'>
                                        <Skeleton width={144} height={40} />
                                    </div>
                                    <div className='relative'>
                                        <div className='h-10 flex items-center justify-center'>
                                            <Skeleton width={144} height={40} />
                                        </div>
                                    </div>
                                    <div className='action'>
                                        <Skeleton width={144} height={40} />
                                    </div>
                                </>
                            ) : (
                                <>
                                    {/* {showStickySortingRow ? (
                                        <div className='action tree-select'>
                                            <TreeSelect
                                                key={`length-${locations?.map((item) => item.parentId)}`}
                                                langCode={langCode}
                                                textOptions={{
                                                    headerLabel: translate('Location'),
                                                    inputPlaceholder: translate('All locations'),
                                                    maxLimitText: translate('You have selected up to three locations')
                                                }}
                                                options={mappedLocations}
                                                childrenList={locationDistricts}
                                                value={locations}
                                                onValueChange={(value) => {
                                                    handleFilterChange(
                                                        {
                                                            locationId: value.map((item) => item.parentId),
                                                            locations: value,
                                                            page: 0
                                                        },
                                                        'FilterBar:Select:Location'
                                                    )
                                                }}
                                                isEnableFooter
                                                dropdownStyleOptions={{
                                                    width: '350px'
                                                }}
                                                floatingConfig={{
                                                    isFlip: false,
                                                    position: 'fixed'
                                                }}
                                                styleOptions={{
                                                    placeholderColor: '#333'
                                                }}
                                                isMobile={windowWidth <= 992}
                                            />
                                        </div>
                                    ) : null} */}
                                    <div>
                                        <JobFunction
                                            hiddenFooter
                                            langCode={langCode}
                                            isMobile={windowWidth <= 992}
                                            isEnableFooter={true}
                                            data={{ ...jobFunctions, jobCount: jobCount }}
                                            value={jobFunctionId}
                                            placeholderColor='#333'
                                            selectStyle={{ maxWidth: 115 }}
                                            onChange={(value) => {
                                                handleFilterChange(
                                                    {
                                                        jobFunctionId: value,
                                                        page: 0
                                                    },
                                                    'FilterBar:Select:Category'
                                                )
                                            }}
                                            floatingConfig={{
                                                isFlip: false,
                                                position: 'fixed'
                                            }}
                                            msBaseUrl={process.env.MICROSERVICE_DOMAIN ?? ''}
                                            selectLayout={{ type: 'highlight' }}
                                        />
                                    </div>

                                    {/* Company industry */}
                                    {windowWidth > 1024 ? (
                                        <div className='action'>
                                            <CompanyIndustry
                                                isMobile={false}
                                                options={[
                                                    { id: -1, nameEn: 'All Industries', name: 'Tất cả lĩnh vực' },
                                                    ...industries
                                                ]}
                                                value={companyIndustries.length > 0 ? companyIndustries[0] : -1}
                                                onChange={(e) =>
                                                    handleFilterChange(
                                                        { companyIndustries: e[0] === -1 ? [] : e, page: 0 },
                                                        'FilterBar:Select:CompanyIndustries'
                                                    )
                                                }
                                                selectStyle={{ maxWidth: 180 }}
                                                dropdownStyle={{ width: 348 }}
                                                floatingConfig={{
                                                    position: 'fixed'
                                                }}
                                                langCode={langCode}
                                                msBaseUrl={process.env.MICROSERVICE_DOMAIN ?? ''}
                                                isEnableSearchAPI
                                                selectLayout={{ type: 'highlight' }}
                                                notCountedAsValue={[-1]}
                                                placeholder={translate('All industries')}
                                                placeholderColor='#333'
                                            />
                                        </div>
                                    ) : (
                                        <></>
                                    )}
                                    {/* Industry */}
                                    {windowWidth > 1366 ? (
                                        <div className='action'>
                                            <MultipleRadio
                                                name='Level'
                                                dropdown={{ width: '280px' }}
                                                selectStyle={{ maxWidth: 180 }}
                                                options={renderLevel}
                                                value={jobLevelId}
                                                onChange={(response) =>
                                                    handleFilterChange(
                                                        { jobLevelId: response.value as number, page: 0 },
                                                        'FilterBar:Select:JobLevel'
                                                    )
                                                }
                                                selectLayout={{ type: 'highlight' }}
                                                notCountedAsValue={[-1]}
                                                placeholder={translate('All levels')}
                                                placeholderColor='#333'
                                            />
                                        </div>
                                    ) : (
                                        <></>
                                    )}

                                    {isQuickSalaryFilterLoading && (
                                        <div className='relative'>
                                            <div className='h-10 flex items-center justify-center'>
                                                <Skeleton width={120} height={40} />
                                            </div>
                                        </div>
                                    )}

                                    {shouldShowQuickSalaryFilter && <div className='relative'>
                                        <StyledFilterSalary
                                            isHighlight={isQuickSalaryTooltipVisible && !isQuickSalaryFilterOn}
                                            isActive={isQuickSalaryFilterOn && !isQuickSalaryTooltipVisible}
                                            onClick={(e) => {
                                                const target = e.target as HTMLElement
                                                const clickedOnSwitcher = switcherRef.current?.contains(target) || false

                                                if (clickedOnSwitcher) {
                                                    return
                                                }

                                                handleQuickSalaryFilterToggle(!isQuickSalaryFilterOn)
                                            }}
                                        >
                                            {isQuickSalaryTooltipVisible && !isQuickSalaryFilterOn && (
                                                <svg height='100%' width='100%'>
                                                    <rect
                                                        rx='8'
                                                        ry='8'
                                                        className={'line'}
                                                        height='100%'
                                                        width='100%'
                                                        strokeLinejoin={'round'}
                                                    />
                                                </svg>
                                            )}
                                            <div className='flex items-center gap-2 px-2 relative z-[1]'>
                                                <span style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>
                                                    {quickSalaryFilterConfig.label}
                                                </span>
                                                <div ref={switcherRef}>
                                                    <Switcher
                                                        value={isQuickSalaryFilterOn}
                                                        onChange={handleQuickSalaryFilterToggle}
                                                    />
                                                </div>
                                            </div>
                                        </StyledFilterSalary>
                                        {isQuickSalaryTooltipVisible && !isQuickSalaryFilterOn && (
                                            <div
                                                className='absolute top-full left-1/2 transform -translate-x-1/2 mt-2 z-[200] pointer-events-auto'
                                            >
                                                <Tooltip
                                                    isShow={isQuickSalaryTooltipVisible}
                                                    description={renderQuickSalaryTooltipDes()}
                                                    customStyle={{
                                                        marginTop: '20px',
                                                        padding: '15px',
                                                        maxWidth: 500,
                                                        borderRadius: '12px',
                                                        boxShadow: '0px 4px 40px 0px rgba(0, 0, 0, 0.16)',
                                                        bgColor: colors.primary[40]
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </div>}

                                    {/* Urgent Hiring */}
                                    <FilterWrapper style={{ width: 'auto' }}>
                                        <SingleCheckBox
                                            id='urgent-hiring'
                                            label={translate('Urgent hiring')}
                                            checked={isUrgentJob}
                                            onChecked={(checked) =>
                                                delayedFireGlobalSearchEvent(
                                                    { isUrgentJob: !!checked, page: 0 },
                                                    'FilterBar:Urgent'
                                                )
                                            }
                                            hoverBgOff
                                            aria-label={translate('Urgent hiring')}
                                            isChecked={isUrgentJob}
                                            hasBorder={true}
                                        />
                                    </FilterWrapper>
                                    {filterNumber > 0 && !isExperiment8VersionA ? (
                                        <Button
                                            type='default'
                                            outline='none'
                                            onClick={() => handleClickClearAll()}
                                            disabled={filterNumber === 0}
                                        >
                                            {translate('Clear')}
                                        </Button>
                                    ) : null}
                                </>
                            )}
                        </div>
                        <div className='right-bar'>
                            <MoreFilterButton
                                filterNumber={filterNumber}
                                handleClickClearAll={handleClickClearAll}
                                handleMoreFilterApply={handleMoreFilterApply}
                                onClose={handleOnClose}
                                onOpen={handleOnOpen}
                                langCode={langCode}
                                customOptions={[
                                    <div key='key-job-function' className='dropdown-row'>
                                        <MoreTitle>{translate('Job function')}</MoreTitle>
                                        <JobFunction
                                            langCode={langCode}
                                            isMobile={windowWidth <= 992}
                                            isEnableFooter={true}
                                            data={{ ...jobFunctions, jobCount: jobCount }}
                                            value={moreFilterSaveOptions.jobFunctionId}
                                            placeholderColor='#333'
                                            onChange={(value) => {
                                                setMoreFilterSaveOptions({
                                                    ...moreFilterSaveOptions,
                                                    jobFunctionId: value
                                                })
                                            }}
                                            dropdownStyleOptions={{
                                                width: '100%'
                                            }}
                                            isFlex
                                            floatingConfig={{
                                                position: 'fixed',
                                                enablePortal: true,
                                                useWindowAsFlippingContainer: true,
                                                isFlip: false,
                                                placement: 'bottom-end'
                                            }}
                                            textOptions={{
                                                placeholder: translate('All functions'),
                                                primaryBtnText: translate('Done')
                                            }}
                                            styleOptions={{
                                                isShowSecondaryBtn: false
                                            }}
                                            msBaseUrl={process.env.MICROSERVICE_DOMAIN ?? ''}
                                            displayAsPopup
                                            selectLayout={{ type: 'highlight' }}
                                        />
                                    </div>,
                                    <div key='key-company-industry' className='dropdown-row'>
                                        <MoreTitle>{translate('Industry')}</MoreTitle>
                                        <CompanyIndustry
                                            isMobile={false}
                                            options={[
                                                { id: -1, nameEn: 'All Industries', name: 'Tất cả lĩnh vực công ty' },
                                                ...industries
                                            ]}
                                            value={companyIndustriesValue}
                                            onChange={(e) => {
                                                setMoreFilterSaveOptions({
                                                    ...moreFilterSaveOptions,
                                                    companyIndustries: e[0] === -1 ? [] : e
                                                })
                                            }}
                                            floatingConfig={{
                                                position: 'fixed',
                                                enablePortal: true,
                                                useWindowAsFlippingContainer: true
                                            }}
                                            dropdownStyle={{
                                                zIndex: 1101
                                            }}
                                            langCode={langCode}
                                            msBaseUrl={process.env.MICROSERVICE_DOMAIN ?? ''}
                                            isEnableSearchAPI
                                            selectLayout={{ type: 'highlight' }}
                                            notCountedAsValue={[-1]}
                                            placeholder={translate('All industries')}
                                            placeholderColor='#333'
                                        />
                                    </div>,
                                    <div key='key-level' className='dropdown-row'>
                                        <MoreTitle>{translate('Levels')}</MoreTitle>
                                        <MultipleRadio
                                            name='Level'
                                            options={renderLevel}
                                            value={moreFilterSaveOptions.jobLevelId}
                                            onChange={(response) =>
                                                setMoreFilterSaveOptions({
                                                    ...moreFilterSaveOptions,
                                                    jobLevelId: response.value as number
                                                })
                                            }
                                            dropdown={{
                                                floatingConfig: {
                                                    position: 'fixed',
                                                    enablePortal: true,
                                                    useWindowAsFlippingContainer: true
                                                }
                                            }}
                                            dropdownStyle={{
                                                zIndex: 1101
                                            }}
                                            selectLayout={{ type: 'highlight' }}
                                            notCountedAsValue={[-1]}
                                            placeholder={translate('All levels')}
                                            placeholderColor='#333'
                                        />
                                    </div>,
                                    <div key='key-working-type' className='dropdown-row'>
                                        <MoreTitle>{translate('Working type')}</MoreTitle>
                                        <MultipleRadio
                                            name='WorkingType'
                                            options={[
                                                { id: ALL_VALUE, name: translate('All working type') },
                                                ...typeWorking
                                            ]}
                                            value={moreFilterSaveOptions.typeWorkingId}
                                            onChange={(response) =>
                                                setMoreFilterSaveOptions({
                                                    ...moreFilterSaveOptions,
                                                    typeWorkingId: response.value as number
                                                })
                                            }
                                            dropdown={{
                                                floatingConfig: {
                                                    position: 'fixed',
                                                    enablePortal: true,
                                                    useWindowAsFlippingContainer: true
                                                }
                                            }}
                                            dropdownStyle={{
                                                zIndex: 1101
                                            }}
                                            selectLayout={{ type: 'highlight' }}
                                            notCountedAsValue={[-1]}
                                            placeholder={translate('All working type')}
                                            placeholderColor='#333'
                                        />
                                    </div>,
                                    <div key='key-job-salary-range' className='dropdown-row'>
                                        <MoreTitle>{translate('Job salary ranges')}</MoreTitle>
                                        <MultipleRadio
                                            name='Salary'
                                            options={normalizedSalaryOptions}
                                            value={moreFilterSalaryValue}
                                            onChange={(response) => handleSalaryChange(response)}
                                            dropdown={{
                                                floatingConfig: {
                                                    position: 'fixed',
                                                    enablePortal: true,
                                                    useWindowAsFlippingContainer: true
                                                }
                                            }}
                                            dropdownStyle={{
                                                zIndex: 1101
                                            }}
                                            selectLayout={{ type: 'highlight' }}
                                            notCountedAsValue={[-1]}
                                            placeholder={translate('All salary ranges')}
                                            placeholderColor='#333'
                                        />
                                    </div>,
                                    <div key='key-job-benefits' className='dropdown-row'>
                                        <MoreTitle>
                                            {translate('Job Benefits')}
                                            {moreFilterSaveOptions.benefit.length > 0 ? (
                                                <>
                                                    <SelectedBenefit>
                                                        {`${moreFilterSaveOptions.benefit.length} ${benefitSelected}`}
                                                    </SelectedBenefit>
                                                    <Clear onClick={handleClearBenefit}>{translate('Clear')}</Clear>
                                                </>
                                            ) : (
                                                <></>
                                            )}
                                        </MoreTitle>
                                        <MultiTagSelectV2
                                            onChange={(ids) => handleBenefitChange(ids)}
                                            values={moreFilterSaveOptions.benefit}
                                            numOfColumns={3}
                                            langCode={langCode}
                                            maxSelect={15}
                                        />
                                    </div>
                                ]}
                                urgentJobComponent={
                                    <FilterWrapper style={{ width: langCode === ELanguage.VI ? 115 : 130 }}>
                                        <CheckBox
                                            label={translate('Urgent hiring')}
                                            state={moreFilterSaveOptions.isUrgentJob ? 'checked' : 'none'}
                                            onCheckChange={(value) => {
                                                setMoreFilterSaveOptions({
                                                    ...moreFilterSaveOptions,
                                                    isUrgentJob: value === 'checked'
                                                })
                                            }}
                                        />
                                    </FilterWrapper>
                                }
                            />
                        </div>
                    </div>
                    <div ref={sortDesktopRef}>
                        {isExperiment8VersionA && showStickySortingRow ? (
                            <div className='desktop-sort-extra-row'>
                                {isExperiment8VersionA ? (
                                    <div className='filter-bar-extra-filters-row'>
                                        <div
                                            className={`filter-bar-extra-filters-dashed${
                                                hideExperiment8FilterHighlight
                                                    ? ' filter-bar-extra-filters-dashed--no-highlight'
                                                    : ''
                                            }`}
                                        >
                                            <svg
                                                className='filter-bar-extra-filters-dashed-svg'
                                                width='100%'
                                                height='100%'
                                                aria-hidden
                                            >
                                                <rect
                                                    className='filter-bar-extra-filters-dashed-line'
                                                    x='0'
                                                    y='0'
                                                    width='100%'
                                                    height='100%'
                                                    rx='8'
                                                    ry='8'
                                                    vectorEffect='nonScalingStroke'
                                                    strokeLinecap='round'
                                                    strokeLinejoin='round'
                                                />
                                            </svg>
                                            <div className='filter-bar-extra-filters-dashed-inner'>
                                                <SortBarExtraFilterRows
                                                    variant='desktop'
                                                    translate={translate}
                                                    language={langCode}
                                                    forceDropdown
                                                    inlineDropdowns
                                                    hideInlineClearButton
                                                    showInlineClearButton={hasClearableFilterActive}
                                                    onInlineClearAllFilters={handleInlineClearAllFilters}
                                                />
                                            </div>
                                        </div>
                                        {hasClearableFilterActive ? (
                                            <button
                                                type='button'
                                                className='filter-bar-extra-filters-clear'
                                                onClick={handleInlineClearAllFilters}
                                            >
                                                {langCode === ELanguage.VI ? 'Xoá bộ lọc' : 'Clear filters'}
                                            </button>
                                        ) : null}
                                        {!hideExperiment8FilterHighlight ? (
                                            <div className='filter-bar-extra-filters-callout'>
                                                <p className='filter-bar-extra-filters-callout-text'>
                                                    {langCode === ELanguage.EN
                                                        ? 'Select to view the newest and least competitive jobs'
                                                        : 'Chọn để xem các việc làm mới và ít cạnh tranh nhất'}
                                                </p>
                                                <button
                                                    type='button'
                                                    className='filter-bar-extra-filters-callout-close'
                                                    aria-label='Đóng'
                                                    onClick={dismissExtraFilterCallout}
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        ) : null}
                                    </div>
                                ) : null}
                            </div>
                        ) : null}
                    </div>
                </div>

                <div className='mobile' style={isFilterBarFixed ? styleSortingFixed : null}>
                    <div className='mobile-sort-bar-row'>
                        <div onClick={onClickFilter}>
                            {filterNumber > 0 ? <FilterWithNumber filterNumber={filterNumber} /> : <IconFilter />}
                            <span className='label'>{translate('Filter')}</span>
                        </div>
                        <div ref={sortMobileRef} style={{ position: 'relative' }}>
                            <div onClick={onClickSort} className={'sortMobileContains'}>
                                <IconSortDesc />
                                <span className='label'>{handleLabelSort()}</span>
                            </div>
                            {isShowTooltip ? (
                                <Tooltip
                                    isShow={isHighlightTooltip && sortId !== SORT_RELEVANCE_INDEX}
                                    description={renderTooltipDes()}
                                    placement={'customSort'}
                                    customStyle={{
                                        marginTop: '20px',
                                        padding: '15px',
                                        width: 'auto',
                                        borderRadius: '12px',
                                        boxShadow: '0px 4px 40px 0px rgba(0, 0, 0, 0.16);',
                                        arrowPosition: '75%',
                                        bgColor: colors.primary[40]
                                    }}
                                />
                            ) : null}
                        </div>
                    </div>
                </div>
            </StyledFilterBar>
        </>
    )
}

function FilterWithNumber({ filterNumber }: { filterNumber: number }) {
    return (
        <div className='filter-filled'>
            <IconFilterFilled />
            <span className='filter-number'>{filterNumber}</span>
        </div>
    )
}
